import {
  createContext,
  forwardRef,
  useContext,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from './cn.js';

/**
 * Form field plumbing.
 *
 * `Field` owns the id, the label association, the hint and the error, and
 * publishes them through context so the control inside does not have to be
 * told its own id or wire up aria-describedby by hand. That is the difference
 * between a component set and a pile of styled inputs: the accessible wiring
 * cannot be forgotten because the caller never does it.
 */

interface FieldCtx {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}

const Ctx = createContext<FieldCtx | null>(null);

function useField(): FieldCtx | null {
  return useContext(Ctx);
}

export interface FieldProps {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  /** Lays the label beside the control instead of above it. */
  horizontal?: boolean;
  className?: string;
  children: ReactNode;
}

export function Field({
  label,
  hint,
  error,
  required,
  horizontal,
  className,
  children,
}: FieldProps) {
  const base = useId();
  const id = `f-${base}`;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;

  return (
    <Ctx.Provider value={{ id, describedBy, invalid: Boolean(error) }}>
      <div
        className={cn(
          horizontal ? 'grid grid-cols-[9rem_1fr] items-start gap-3' : 'space-y-1.5',
          className,
        )}
      >
        {label && (
          <label
            htmlFor={id}
            className={cn(
              'block text-xs font-medium text-ink-secondary',
              horizontal && 'pt-1.5',
            )}
          >
            {label}
            {required && (
              <span aria-hidden className="ml-0.5 text-danger">
                *
              </span>
            )}
          </label>
        )}
        <div className={cn(!horizontal && 'space-y-1.5')}>
          {children}
          {error ? (
            <p id={errorId} className="flex items-start gap-1 text-2xs text-danger">
              <AlertCircle aria-hidden className="mt-px h-3 w-3 shrink-0" />
              {error}
            </p>
          ) : (
            hint && (
              <p id={hintId} className="text-2xs leading-snug text-ink-tertiary">
                {hint}
              </p>
            )
          )}
        </div>
      </div>
    </Ctx.Provider>
  );
}

/** Shared control chrome: one definition, so every input matches. */
export const controlBase = cn(
  'w-full bg-surface text-ink',
  'rounded-md ring-1 ring-inset ring-line-strong/70',
  'transition-[box-shadow,background-color] duration-fast',
  'hover:ring-line-strong',
  'focus:outline-none focus:ring-2 focus:ring-brand',
  'disabled:cursor-not-allowed disabled:bg-surface-sunken disabled:text-ink-tertiary',
  'aria-[invalid=true]:ring-danger',
);

export const controlSize = {
  sm: 'h-7 px-2 text-xs',
  md: 'h-8 px-2.5 text-sm',
  lg: 'h-9 px-3 text-base',
} as const;

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  size?: keyof typeof controlSize;
  leading?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size = 'md', leading, ...rest },
  ref,
) {
  const f = useField();
  const input = (
    <input
      ref={ref}
      id={rest.id ?? f?.id}
      aria-describedby={rest['aria-describedby'] ?? f?.describedBy}
      aria-invalid={rest['aria-invalid'] ?? (f?.invalid || undefined)}
      className={cn(controlBase, controlSize[size], leading && 'pl-7', className)}
      {...rest}
    />
  );

  if (!leading) return input;
  return (
    <div className="relative">
      <span
        aria-hidden
        className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-ink-tertiary [&>svg]:h-3.5 [&>svg]:w-3.5"
      >
        {leading}
      </span>
      {input}
    </div>
  );
});

export interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { className, rows = 4, ...rest },
  ref,
) {
  const f = useField();
  return (
    <textarea
      ref={ref}
      rows={rows}
      id={rest.id ?? f?.id}
      aria-describedby={rest['aria-describedby'] ?? f?.describedBy}
      aria-invalid={rest['aria-invalid'] ?? (f?.invalid || undefined)}
      className={cn(controlBase, 'resize-y px-2.5 py-2 text-sm leading-relaxed', className)}
      {...rest}
    />
  );
});

export { useField };
